import Raven from 'raven-js';
import { transformStoredAttachmentToBlob } from './attachmentTransformer';

/**
 * Extracts the optional error from the given event.
 *
 * @param event The event.
 * @returns {null} The corresponding error object.
 */
const getError = (event) => {
  if (event.target) {
    return event.target.error;
  }
  return null;
};

/**
 * Logs the error in the browser console and sends an event to Sentry.
 *
 * @param msg The error message.
 * @param event The error event.
 */
const logError = (msg, event) => {
  console.error(new Date() + msg, event.target);
  const errorCode = event.target && event.target.error && event.target.error.message ?
    event.target.error.message : 'No error message';
  Raven.captureMessage(msg + ': ' + errorCode, {
    eventTarget: event.target,
  });
  if (event.target && event.target.error) {
    Raven.captureException(event.target.error);
  }
};

class Attachments {
  db = null;
  dbVersion = 1;

  initialize = () => new Promise((resolve, reject) => {
    if (this.db != null) {
      resolve(this.db);
      return;
    }

    const request = window.indexedDB.open('book-attachments', this.dbVersion);
    request.onupgradeneeded = (event) => {
      console.info('IndexedDB onupgradeneeded ', event);
      const db = event.target.result;
      let oldVersion = event.oldVersion;
      if (!oldVersion || oldVersion > 1000) {
        // Webkit bug https://bugs.webkit.org/show_bug.cgi?id=136888
        oldVersion = 0;
      }

      if (oldVersion < 1) {
        db.createObjectStore('document', { keyPath: 'id' });
      }
    };

    /**
     * Function called when opening the database is successful.
     */
    request.onsuccess = (event) => {
      console.info('IndexedDB database opened');
      this.db = event.target.result;

      /**
       * Global error event listener for the connection.
       *
       * When there is an error, if the "request.onerror" is defined, it
       * will be called. Otherwise, this callback will be called.
       */
      this.db.onerror = (e) => {
        logError(
          'Error while interacting with IndexedDB database',
          e
        );
      };

      /*
       * Listens for the "close" event on the connection object.
       *
       * null is then assigned to the connection variable to cause a
       * new connection to be established the next time an access to
       * IndexedDB is required.
       *
       * It is supported in Chrome 31, Firefox 50, and Safari 10.1
       * See https://www.w3.org/TR/IndexedDB/#dom-idbdatabase-close
       */
      const self = this;
      this.db.onclose = () => {
        console.info('The IndexedDB database is closed');
        self.db = null;
      };

      resolve(this.db);
    };

    /**
     * Callback for error when opening the database
     */
    request.onerror = (event) => {
      const msg = 'Error while opening the IndexedDB database';
      logError(
        msg,
        event
      );
      reject({
        msg,
      });
    };
  });

  saveAttachment = (id, arrayBuffer, contentType) => new Promise((resolve, reject) => {
    console.info(`Storing array buffer for id ${id}`);
    const objectToStore = {
      id,
      data: arrayBuffer,
      type: contentType,
    };

    this.initialize().then((db) => {
      const transaction = db.transaction(['document'], 'readwrite');
      transaction.onerror = (event) => {
        logError('Error saving the attachment', event);
        reject(getError(event));
      };

      transaction.onabort = (event) => {
        logError('Transaction aborted', event);
        reject(getError(event));
      };

      const store = transaction.objectStore('document');

      const getRequest = store.get(id);
      getRequest.onsuccess = () => {
        if (getRequest.result) {
          const putRequest = store.put(objectToStore);
          putRequest.onsuccess = () => {
            console.info('Document updated');
            resolve();
          };
        } else {
          const addRequest = store.add(objectToStore);
          addRequest.onsuccess = () => {
            console.info('Document added');
            resolve();
          };
        }
      };
    }).catch((errorMsg) => {
      reject(errorMsg);
    });
  });

  loadAttachment = (id) => {
    console.info(`Loading blob for id ${id}`);
    return new Promise((resolve, reject) => {
      this.initialize().then((db) => {
        const transaction = db.transaction(['document'], 'readonly');
        const errorHandler = (event) => {
          logError('Error reading the attachment', event);
          reject(event);
        };
        transaction.onerror = errorHandler;
        transaction.onabort = errorHandler;

        const store = transaction.objectStore('document');
        const request = store.get(id);
        request.onsuccess = () => {
          try {
            resolve(
              request.result && transformStoredAttachmentToBlob(request.result));
          } catch (e) {
            console.error('Error decoding the attachment', e);
            reject(e);
          }
        };
      }).catch((errorMsg) => {
        reject(errorMsg);
      });
    });
  };

  deleteAttachments = (ids) => {
    console.info(`Deleting blobs with ids ${ids}`);
    return new Promise((resolve, reject) => {
      this.initialize().then((db) => {
        const transaction = db.transaction(['document'], 'readwrite');
        transaction.onerror = (event) => {
          logError('Error deleting the attachments', event);
          reject(event);
        };

        const store = transaction.objectStore('document');
        let nbResolved = 0;
        ids.forEach((id) => {
          const request = store.delete(id);
          request.onsuccess = () => {
            nbResolved += 1;
            if (nbResolved === ids.length) {
              resolve();
            }
          };
        });
      }).catch((errorMsg) => {
        reject(errorMsg);
      });
    });
  };

  clearAttachments = () => {
    console.info('Clearing attachments');
    return new Promise((resolve, reject) => {
      this.initialize().then((db) => {
        const transaction = db.transaction(['document'], 'readwrite');
        transaction.onerror = (event) => {
          logError('Error clearing the attachments', event);
          reject(event);
        };

        const store = transaction.objectStore('document');
        const request = store.clear();
        request.onsuccess = () => {
          resolve();
        };
      });
    });
  };
}

const attachments = new Attachments();

export { Attachments };
export default attachments;



// WEBPACK FOOTER //
// ./book/src/attachments/attachments.js